import {
  ApprovableType,
  CompleteableType,
  DestroyableType,
  PurchaseType,
  ResolveableType,
  RevertVoidableType,
  SaleType,
  VoidableType,
} from 'api-hooks/general/model';
import { AuthorizationRules } from 'common/constants';
import React from 'react';

import { useAuthorization } from './use-authorization';

export type SectionType =
  | ApprovableType
  | VoidableType
  | SaleType
  | PurchaseType
  | RevertVoidableType
  | ResolveableType
  | CompleteableType
  | 'cheque-notes'
  | DestroyableType;

export default function useGetCrudPermission(type: SectionType) {
  const { can } = useAuthorization();
  const permissions = React.useMemo(() => {
    switch (type) {
      case 'account-payables':
        return {
          create: can(AuthorizationRules.AccountPayablesCreate),
          update: can(AuthorizationRules.AccountPayablesView),
          delete: can(AuthorizationRules.AccountPayablesVoid),
          list: can(AuthorizationRules.AccountPayablesPage),
          view: can(AuthorizationRules.AccountPayablesView),
          page: can(AuthorizationRules.AccountPayablesPage),
        };
      case 'account-receivables':
        return {
          create: can(AuthorizationRules.AccountReceivablesCreate),
          update: can(AuthorizationRules.AccountReceivablesView),
          delete: can(AuthorizationRules.AccountReceivablesVoid),
          list: can(AuthorizationRules.AccountReceivablesList),
          view: can(AuthorizationRules.AccountReceivablesView),
          page: can(AuthorizationRules.AccountReceivablesPage),
        };
      case 'activity-purchases':
        return {
          create:
            can(AuthorizationRules.ActivityPurchasesCreate) ||
            can(AuthorizationRules.PurchasesCore),
          update:
            can(AuthorizationRules.ActivityPurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          delete:
            can(AuthorizationRules.ActivityPurchasesVoid) ||
            can(AuthorizationRules.PurchasesCore),
          list:
            can(AuthorizationRules.ActivityPurchasesList) ||
            can(AuthorizationRules.PurchasesCore),
          view:
            can(AuthorizationRules.ActivityPurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          page:
            can(AuthorizationRules.ActivityPurchasesPage) ||
            can(AuthorizationRules.PurchasesCore),
        };
      case 'activity-sales':
        return {
          create:
            can(AuthorizationRules.ActivitySalesCreate) ||
            can(AuthorizationRules.SalesCore),
          update:
            can(AuthorizationRules.ActivitySalesView) ||
            can(AuthorizationRules.SalesCore),
          delete:
            can(AuthorizationRules.ActivitySalesVoid) ||
            can(AuthorizationRules.SalesCore),
          list:
            can(AuthorizationRules.ActivitySalesList) ||
            can(AuthorizationRules.SalesCore),
          view:
            can(AuthorizationRules.ActivitySalesView) ||
            can(AuthorizationRules.SalesCore),
          page:
            can(AuthorizationRules.ActivitySalesPage) ||
            can(AuthorizationRules.SalesCore),
        };
      case 'balance-adjustments':
        return {
          create: can(AuthorizationRules.BalanceAdjustmentsCreate),
          update: can(AuthorizationRules.BalanceAdjustmentsView),
          delete: can(AuthorizationRules.BalanceAdjustmentsVoid),
          list: can(AuthorizationRules.BalanceAdjustmentsList),
          view: can(AuthorizationRules.BalanceAdjustmentsView),
          page: can(AuthorizationRules.BalanceAdjustmentsPage),
        };
      case 'bank-transactions':
        return {
          create: can(AuthorizationRules.BankTransactionsCreate),
          update: can(AuthorizationRules.BankTransactionsView),
          delete: can(AuthorizationRules.BankTransactionsVoid),
          list: can(AuthorizationRules.BankTransactionsList),
          view: can(AuthorizationRules.BankTransactionsView),
          page: can(AuthorizationRules.BankTransactionsPage),
        };
      case 'cash-transactions':
        return {
          create: can(AuthorizationRules.CashTransactionsCreate),
          update: can(AuthorizationRules.CashTransactionsView),
          delete: can(AuthorizationRules.CashTransactionsVoid),
          list: can(AuthorizationRules.CashTransactionsList),
          view: can(AuthorizationRules.CashTransactionsView),
          page: can(AuthorizationRules.CashTransactionsPage),
        };
      case 'cruise-purchases':
        return {
          create:
            can(AuthorizationRules.CruisePurchasesCreate) ||
            can(AuthorizationRules.PurchasesCore),
          update:
            can(AuthorizationRules.CruisePurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          delete:
            can(AuthorizationRules.CruisePurchasesVoid) ||
            can(AuthorizationRules.PurchasesCore),
          list:
            can(AuthorizationRules.CruisePurchasesList) ||
            can(AuthorizationRules.PurchasesCore),
          view:
            can(AuthorizationRules.CruisePurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          page:
            can(AuthorizationRules.CruisePurchasesPage) ||
            can(AuthorizationRules.PurchasesCore),
        };
      case 'cruise-sales':
        return {
          create:
            can(AuthorizationRules.CruiseSalesCreate) ||
            can(AuthorizationRules.SalesCore),
          update: false, // no action
          delete:
            can(AuthorizationRules.CruiseSalesVoid) ||
            can(AuthorizationRules.SalesCore),
          list:
            can(AuthorizationRules.CruiseSalesList) ||
            can(AuthorizationRules.SalesCore),
          view:
            can(AuthorizationRules.CruiseSalesView) ||
            can(AuthorizationRules.SalesCore),
          page:
            can(AuthorizationRules.CruiseSalesPage) ||
            can(AuthorizationRules.SalesCore),
        };
      case 'document-purchases':
        return {
          create:
            can(AuthorizationRules.DocumentPurchasesCreate) ||
            can(AuthorizationRules.PurchasesCore),
          update:
            can(AuthorizationRules.DocumentPurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          delete:
            can(AuthorizationRules.DocumentPurchasesVoid) ||
            can(AuthorizationRules.PurchasesCore),
          list:
            can(AuthorizationRules.DocumentPurchasesList) ||
            can(AuthorizationRules.PurchasesCore),
          view:
            can(AuthorizationRules.DocumentPurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          page:
            can(AuthorizationRules.DocumentPurchasesPage) ||
            can(AuthorizationRules.PurchasesCore),
        };
      case 'document-sales':
        return {
          create:
            can(AuthorizationRules.DocumentSalesCreate) ||
            can(AuthorizationRules.SalesCore),
          update: false, // no action
          delete:
            can(AuthorizationRules.DocumentSalesVoid) ||
            can(AuthorizationRules.SalesCore),
          list:
            can(AuthorizationRules.DocumentSalesList) ||
            can(AuthorizationRules.SalesCore),
          view:
            can(AuthorizationRules.DocumentSalesView) ||
            can(AuthorizationRules.SalesCore),
          page:
            can(AuthorizationRules.DocumentSalesPage) ||
            can(AuthorizationRules.SalesCore),
        };
      case 'flight-purchases':
        return {
          create:
            can(AuthorizationRules.FlightPurchasesCreate) ||
            can(AuthorizationRules.PurchasesCore),
          update:
            can(AuthorizationRules.FlightPurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          delete:
            can(AuthorizationRules.FlightPurchasesVoid) ||
            can(AuthorizationRules.PurchasesCore),
          list:
            can(AuthorizationRules.FlightPurchasesList) ||
            can(AuthorizationRules.PurchasesCore),
          view:
            can(AuthorizationRules.FlightPurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          page:
            can(AuthorizationRules.FlightPurchasesPage) ||
            can(AuthorizationRules.PurchasesCore),
        };
      case 'flight-sales':
        return {
          create:
            can(AuthorizationRules.FlightSalesCreate) ||
            can(AuthorizationRules.SalesCore),
          update: false, // no action
          delete:
            can(AuthorizationRules.FlightSalesVoid) ||
            can(AuthorizationRules.SalesCore),
          list:
            can(AuthorizationRules.FlightSalesList) ||
            can(AuthorizationRules.SalesCore),
          view:
            can(AuthorizationRules.FlightSalesView) ||
            can(AuthorizationRules.SalesCore),
          page:
            can(AuthorizationRules.FlightSalesPage) ||
            can(AuthorizationRules.SalesCore),
        };
      case 'hotel-purchases':
        return {
          create:
            can(AuthorizationRules.HotelPurchasesCreate) ||
            can(AuthorizationRules.PurchasesCore),
          update:
            can(AuthorizationRules.HotelPurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          delete:
            can(AuthorizationRules.HotelPurchasesVoid) ||
            can(AuthorizationRules.PurchasesCore),
          list:
            can(AuthorizationRules.HotelPurchasesList) ||
            can(AuthorizationRules.PurchasesCore),
          view:
            can(AuthorizationRules.HotelPurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          page:
            can(AuthorizationRules.HotelPurchasesPage) ||
            can(AuthorizationRules.PurchasesCore),
        };
      case 'hotel-sales':
        return {
          create:
            can(AuthorizationRules.HotelSalesCreate) ||
            can(AuthorizationRules.SalesCore),
          update: false, // no action
          delete:
            can(AuthorizationRules.HotelSalesVoid) ||
            can(AuthorizationRules.SalesCore),
          list:
            can(AuthorizationRules.HotelSalesList) ||
            can(AuthorizationRules.SalesCore),
          view:
            can(AuthorizationRules.HotelSalesView) ||
            can(AuthorizationRules.SalesCore),
          page:
            can(AuthorizationRules.HotelSalesPage) ||
            can(AuthorizationRules.SalesCore),
        };
      case 'order-adjustments':
        return {
          create: can(AuthorizationRules.OrderAdjustmentsCreate),
          update: false, // no action
          delete: can(AuthorizationRules.OrderAdjustmentsVoid),
          list: can(AuthorizationRules.OrderAdjustmentsList),
          view: can(AuthorizationRules.OrderAdjustmentsView),
          page: can(AuthorizationRules.OrderAdjustmentsPage),
        };
      case 'other-expenses':
        return {
          create: can(AuthorizationRules.OtherExpensesCreate),
          update: can(AuthorizationRules.OtherExpensesUpdate),
          delete: can(AuthorizationRules.OtherExpensesVoid),
          list:
            can(AuthorizationRules.SalesGetOtherExpenses) ||
            can(AuthorizationRules.ToursGetOtherExpenses),
          view: can(AuthorizationRules.OtherExpensesView),
          page: false, // no action
        };
      case 'other-incomes':
        return {
          create: can(AuthorizationRules.OtherIncomesCreate),
          update: can(AuthorizationRules.OtherIncomesUpdate),
          delete: can(AuthorizationRules.OtherIncomesVoid),
          list:
            can(AuthorizationRules.SalesGetOtherIncomes) ||
            can(AuthorizationRules.ToursGetOtherIncomes),
          view: can(AuthorizationRules.OtherIncomesView),
          page: false, // no action
        };
      case 'product-purchases':
        return {
          create:
            can(AuthorizationRules.ProductPurchasesCreate) ||
            can(AuthorizationRules.PurchasesCore),
          update:
            can(AuthorizationRules.ProductPurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          delete:
            can(AuthorizationRules.ProductPurchasesVoid) ||
            can(AuthorizationRules.PurchasesCore),
          list:
            can(AuthorizationRules.ProductPurchasesList) ||
            can(AuthorizationRules.PurchasesCore),
          view:
            can(AuthorizationRules.ProductPurchasesView) ||
            can(AuthorizationRules.PurchasesCore),
          page:
            can(AuthorizationRules.ProductPurchasesPage) ||
            can(AuthorizationRules.PurchasesCore),
        };
      case 'product-sales':
        return {
          create:
            can(AuthorizationRules.ProductSalesCreate) ||
            can(AuthorizationRules.SalesCore),
          update: false, // no action
          delete:
            can(AuthorizationRules.ProductSalesVoid) ||
            can(AuthorizationRules.SalesCore),
          list:
            can(AuthorizationRules.ProductSalesList) ||
            can(AuthorizationRules.SalesCore),
          view:
            can(AuthorizationRules.ProductSalesView) ||
            can(AuthorizationRules.SalesCore),
          page:
            can(AuthorizationRules.ProductSalesPage) ||
            can(AuthorizationRules.SalesCore),
        };
      case 'receipts':
        return {
          create: can(AuthorizationRules.ReceiptsCreate),
          update: false, // no action
          delete: can(AuthorizationRules.ReceiptsVoid),
          list: can(AuthorizationRules.ReceiptsList),
          view: can(AuthorizationRules.ReceiptsView),
          page: can(AuthorizationRules.ReceiptsPage),
        };
      case 'tour-sales':
        return {
          create:
            can(AuthorizationRules.TourSalesCreate) ||
            can(AuthorizationRules.SalesCore),
          update:
            can(AuthorizationRules.TourSalesView) ||
            can(AuthorizationRules.SalesCore),
          delete:
            can(AuthorizationRules.TourSalesVoid) ||
            can(AuthorizationRules.SalesCore),
          list:
            can(AuthorizationRules.TourSalesList) ||
            can(AuthorizationRules.SalesCore),
          view:
            can(AuthorizationRules.TourSalesView) ||
            can(AuthorizationRules.SalesCore),
          page:
            can(AuthorizationRules.TourSalesPage) ||
            can(AuthorizationRules.SalesCore),
        };
      case 'transfers':
        return {
          create: can(AuthorizationRules.TransfersCreate),
          update: can(AuthorizationRules.TransfersView),
          delete: can(AuthorizationRules.TransfersVoid),
          list: can(AuthorizationRules.TransfersList),
          view: can(AuthorizationRules.TransfersView),
          page: can(AuthorizationRules.TransfersPage),
        };
      case 'tours':
        return {
          create: can(AuthorizationRules.ToursCreate),
          update: can(AuthorizationRules.ToursView),
          delete: false, // no action
          list: can(AuthorizationRules.ToursList),
          view: can(AuthorizationRules.ToursView),
          page: can(AuthorizationRules.ToursPage),
        };
      case 'refunds':
        return {
          create: can(AuthorizationRules.RefundsCreate),
          update: can(AuthorizationRules.RefundsUpdate),
          delete: false, // no action
          list: can(AuthorizationRules.RefundsList),
          view: can(AuthorizationRules.RefundsView),
          page: can(AuthorizationRules.RefundsPage),
        };
      case 'release-purchases':
        return {
          create: false, // no action
          update: false, // no action
          delete: false, // no action
          list: can(AuthorizationRules.ReleasePurchasesList),
          view: can(AuthorizationRules.ReleasePurchasesList),
          page: can(AuthorizationRules.ReleasePurchasesList),
        };
      case 'release-sales':
        return {
          create: false, // no action
          update: false, // no action
          delete: false, // no action
          list: can(AuthorizationRules.ReleaseSalesList),
          view: can(AuthorizationRules.ReleaseSalesView),
          page: can(AuthorizationRules.ReleaseSalesPage),
        };
      case 'projects':
        return {
          create: can(AuthorizationRules.ProjectsCreate),
          update: can(AuthorizationRules.ProjectsUpdate),
          delete: false,
          list: can(AuthorizationRules.ProjectsList),
          view: can(AuthorizationRules.ProjectsView),
          page: can(AuthorizationRules.ProjectsPage),
        };
      case 'activities':
        return {
          create: can(AuthorizationRules.ActivitiesCreate),
          update: can(AuthorizationRules.ActivitiesUpdate),
          delete: can(AuthorizationRules.ActivitiesDelete),
          list: can(AuthorizationRules.ActivitiesList),
          view: can(AuthorizationRules.ActivitiesView),
          page: can(AuthorizationRules.ActivitiesPage),
        };
      case 'agents':
        return {
          create: can(AuthorizationRules.AgentsCreate),
          update: can(AuthorizationRules.AgentsUpdate),
          delete: can(AuthorizationRules.AgentsDelete),
          list: can(AuthorizationRules.AgentsList),
          view: can(AuthorizationRules.AgentsView),
          page: can(AuthorizationRules.AgentsPage),
        };
      case 'airlines':
        return {
          create: can(AuthorizationRules.AirlinesCreate),
          update: can(AuthorizationRules.AirlinesUpdate),
          delete: can(AuthorizationRules.AirlinesDelete),
          list: can(AuthorizationRules.AirlinesList),
          view: can(AuthorizationRules.AirlinesView),
          page: can(AuthorizationRules.AirlinesPage),
        };
      case 'approvals':
        return {
          create: can(AuthorizationRules.ApprovalsCreate),
          update: can(AuthorizationRules.ApprovalsUpdate),
          delete: can(AuthorizationRules.ApprovalsDelete),
          list: can(AuthorizationRules.ApprovalsList),
          view: can(AuthorizationRules.ApprovalsView),
          page: can(AuthorizationRules.ApprovalsPage),
        };
      case 'bank-accounts':
        return {
          create: can(AuthorizationRules.BankAccountsCreate),
          update: can(AuthorizationRules.BankAccountsUpdate),
          delete: can(AuthorizationRules.BankAccountsDelete),
          list: can(AuthorizationRules.BankAccountsList),
          view: can(AuthorizationRules.BankAccountsView),
          page: can(AuthorizationRules.BankAccountsPage),
        };
      case 'cash-accounts':
        return {
          create: can(AuthorizationRules.CashAccountsCreate),
          update: can(AuthorizationRules.CashAccountsUpdate),
          delete: can(AuthorizationRules.CashAccountsDelete),
          list: can(AuthorizationRules.CashAccountsList),
          view: can(AuthorizationRules.CashAccountsView),
          page: can(AuthorizationRules.CashAccountsPage),
        };
      case 'cheque-giros':
        return {
          create: can(AuthorizationRules.ChequeGirosCreate),
          update: can(AuthorizationRules.ChequeGirosUpdate),
          delete: can(AuthorizationRules.ChequeGirosDelete),
          list: can(AuthorizationRules.ChequeGirosList),
          view: can(AuthorizationRules.ChequeGirosView),
          page: can(AuthorizationRules.ChequeGirosPage),
        };
      case 'cruises':
        return {
          create: can(AuthorizationRules.CruisesCreate),
          update: can(AuthorizationRules.CruisesUpdate),
          delete: can(AuthorizationRules.CruisesDelete),
          list: can(AuthorizationRules.CruisesList),
          view: can(AuthorizationRules.CruisesView),
          page: can(AuthorizationRules.CruisesPage),
        };
      case 'customers':
        return {
          create: can(AuthorizationRules.CustomersCreate),
          update: can(AuthorizationRules.CustomersUpdate),
          delete: can(AuthorizationRules.CustomersDelete),
          list: can(AuthorizationRules.CustomersList),
          view: can(AuthorizationRules.CustomersView),
          page: can(AuthorizationRules.CustomersPage),
        };
      case 'employees':
        return {
          create: can(AuthorizationRules.EmployeesCreate),
          update: can(AuthorizationRules.EmployeesUpdate),
          delete: can(AuthorizationRules.EmployeesDelete),
          list: can(AuthorizationRules.EmployeesList),
          view: can(AuthorizationRules.EmployeesView),
          page: can(AuthorizationRules.EmployeesPage),
        };
      case 'guests':
        return {
          create: can(AuthorizationRules.GuestsCreate),
          update: can(AuthorizationRules.GuestsUpdate),
          delete: can(AuthorizationRules.GuestsDelete),
          list: can(AuthorizationRules.GuestsList),
          view: can(AuthorizationRules.GuestsView),
          page: can(AuthorizationRules.GuestsPage),
        };
      case 'hotels':
        return {
          create: can(AuthorizationRules.HotelsCreate),
          update: can(AuthorizationRules.HotelsUpdate),
          delete: can(AuthorizationRules.HotelsDelete),
          list: can(AuthorizationRules.HotelsList),
          view: can(AuthorizationRules.HotelsView),
          page: can(AuthorizationRules.HotelsPage),
        };
      case 'payment-channels':
        return {
          create: can(AuthorizationRules.PaymentChannelsCreate),
          update: can(AuthorizationRules.PaymentChannelsUpdate),
          delete: can(AuthorizationRules.PaymentChannelsDelete),
          list: can(AuthorizationRules.PaymentChannelsList),
          view: can(AuthorizationRules.PaymentChannelsView),
          page: can(AuthorizationRules.PaymentChannelsPage),
        };
      case 'preference-groups':
        return {
          create: can(AuthorizationRules.PreferenceGroupsCreate),
          update: can(AuthorizationRules.PreferenceGroupsUpdate),
          delete: can(AuthorizationRules.PreferenceGroupsDelete),
          list: can(AuthorizationRules.PreferenceGroupsList),
          view: can(AuthorizationRules.PreferenceGroupsView),
          page: can(AuthorizationRules.PreferenceGroupsPage),
        };
      case 'products':
        return {
          create: can(AuthorizationRules.ProductsCreate),
          update: can(AuthorizationRules.ProductsUpdate),
          delete: can(AuthorizationRules.ProductsDelete),
          list: can(AuthorizationRules.ProductsList),
          view: can(AuthorizationRules.ProductsView),
          page: can(AuthorizationRules.ProductsPage),
        };
      case 'roles':
        return {
          create: can(AuthorizationRules.RolesCreate),
          update: can(AuthorizationRules.RolesUpdate),
          delete: can(AuthorizationRules.RolesDelete),
          list: can(AuthorizationRules.RolesList),
          view: can(AuthorizationRules.RolesView),
          page: can(AuthorizationRules.RolesPage),
        };
      case 'transaction-categories':
        return {
          create: can(AuthorizationRules.TransactionCategoriesCreate),
          update: can(AuthorizationRules.TransactionCategoriesUpdate),
          delete: can(AuthorizationRules.TransactionCategoriesDelete),
          list: can(AuthorizationRules.TransactionCategoriesList),
          view: can(AuthorizationRules.TransactionCategoriesView),
          page: can(AuthorizationRules.TransactionCategoriesPage),
        };
      case 'tour-categories':
        return {
          create: can(AuthorizationRules.TourCategoriesCreate),
          update: can(AuthorizationRules.TourCategoriesUpdate),
          delete: can(AuthorizationRules.TourCategoriesDelete),
          list: can(AuthorizationRules.TourCategoriesList),
          view: can(AuthorizationRules.TourCategoriesView),
          page: can(AuthorizationRules.TourCategoriesPage),
        };
      case 'visa-documents':
        return {
          create: can(AuthorizationRules.VisaDocumentsCreate),
          update: can(AuthorizationRules.VisaDocumentsUpdate),
          delete: can(AuthorizationRules.VisaDocumentsDelete),
          list: can(AuthorizationRules.VisaDocumentsList),
          view: can(AuthorizationRules.VisaDocumentsView),
          page: can(AuthorizationRules.VisaDocumentsPage),
        };
      case 'commission-masters':
        return {
          create: can(AuthorizationRules.CommissionMastersCreate),
          update: can(AuthorizationRules.CommissionMastersUpdate),
          delete: can(AuthorizationRules.CommissionMastersDelete),
          list: can(AuthorizationRules.CommissionMastersList),
          view: can(AuthorizationRules.CommissionMastersView),
          page: can(AuthorizationRules.CommissionMastersPage),
        };
      //TODO SYNC
      case 'bonus-periods':
        return {
          create: can(AuthorizationRules.BonusPeriodsCreate),
          update: can(AuthorizationRules.BonusPeriodsUpdate),
          delete: can(AuthorizationRules.BonusPeriodsDelete),
          list: can(AuthorizationRules.BonusPeriodsList),
          view: can(AuthorizationRules.BonusPeriodsView),
          page: can(AuthorizationRules.BonusPeriodsPage),
        };
      case 'product-adjustments':
        return {
          create: can(AuthorizationRules.ProductAdjustmentsCreate),
          update: can(AuthorizationRules.ProductAdjustmentsUpdate),
          delete: can(AuthorizationRules.ProductAdjustmentsDelete),
          list: can(AuthorizationRules.ProductAdjustmentsList),
          view: can(AuthorizationRules.ProductAdjustmentsView),
          page: can(AuthorizationRules.ProductAdjustmentsPage),
        };
      case 'cheque-notes':
        return {
          create: false,
          update: can(AuthorizationRules.ChequeNotesUpdateClearing),
          delete: can(AuthorizationRules.ChequeNotesDeleteClearing),
          list: can(AuthorizationRules.ChequeNotesList),
          view: can(AuthorizationRules.ChequeNotesView),
          page: can(AuthorizationRules.ChequeNotesPage),
        };
      default:
        return {};
    }
  }, [can, type]);

  return permissions;
}
